// Plain words for the codes an agent gap carries
// (docs/superpowers/specs/2026-10-15-agent-gaps-design.md, "Categories and reasons"). A code with no
// words yet (one added on the server first) reads as itself.

// In the spec's order: the order of the panel's category chips.
export const CATEGORY_WORDS = {
  no_access: "No access",
  agent_settings: "Agent settings",
  cant_do_yet: "Can't do yet",
  blocked: "Blocked",
  failed: "Failed",
};

export const REASON_WORDS = {
  // no_access
  member_area: "A member can't use this area",
  write_gate: "The user can't make changes here",
  send_gate: "The user can't send this",
  member_schema: "A member can't use this clinic's records",
  planner_area: "Left out of a plan: the user can't use it",
  agent_reported: "The agent thinks the user lacks access",
  policy_area: "The clinic's agent settings turn this area off",
  policy_write: "The clinic's agent settings make this area read-only",
  policy_send: "The clinic's agent settings turn this send off",
  // agent_settings
  scope_area: "The owner turned this area off for the agent",
  scope_schema: "The owner left this clinic out of the agent",
  // cant_do_yet
  no_action: "No action exists for this yet",
  unknown_action: "The agent tried an action that doesn't exist",
  planner_cannot: "Asked for something agents can't do yet",
  unclear: "The agent couldn't tell what was asked",
  // blocked
  needs_approval: "Held for approval in Auto mode",
  action_refused: "A rule refused it",
  action_limit: "The run used all its actions",
  time_limit: "The run ran out of time",
  // failed
  bad_arguments: "The agent used an action wrongly",
  unknown_record: "The agent named a record it hadn't been shown",
  access_unconfirmed: "Access couldn't be confirmed just then",
  privacy_check: "The privacy check couldn't run",
  error: "An error in the action",
  run_failed: "The run failed",
};

// Whether a gap of a reason is usually the agent working as designed, something to build or fix,
// or either (the detail decides), to triage by.
export const VERDICTS = {
  design: { label: "Usually by design", color: "green", says: "Usually working as designed: a rule or a setting stopped it." },
  fix: { label: "Likely needs a fix", color: "red", says: "Usually something to build or fix." },
  check: { label: "Check the detail", color: "yellow", says: "Could go either way: the detail says which." },
};

// For each reason: what happened, in plain words, its usual verdict, and what to do about it.
export const REASON_HELP = {
  // no_access
  member_area: {
    means: "The agent needed an area that someone on it can't use in the app: their role doesn't open that screen.",
    verdict: "design",
    todo: "Fine if they shouldn't have that access. Otherwise change their role, or take them off the agent.",
  },
  write_gate: {
    means: "The person the agent acted for can see this area, but their role can't change things in it.",
    verdict: "design",
    todo: "Fine if they shouldn't make changes there. Otherwise give their role that permission.",
  },
  send_gate: {
    means: "The person the agent acted for isn't allowed to send this kind of message in the app.",
    verdict: "design",
    todo: "Fine if they shouldn't send it. Otherwise allow it for their role.",
  },
  member_schema: {
    means: "Someone on the agent can't see the records of the clinic it used.",
    verdict: "design",
    todo: "Fine if they shouldn't see that clinic. Otherwise add the clinic to their account.",
  },
  planner_area: {
    means: "While an agent was being set up, an area was left out because the person can't use it.",
    verdict: "design",
    todo: "Nothing to do unless their role should include that area.",
  },
  agent_reported: {
    means: "The agent itself said the person doesn't have access to something it needed.",
    verdict: "check",
    todo: "Read the detail. If the person does have that access, the agent got it wrong and its prompt or tools need a fix.",
  },
  policy_area: {
    means: "The clinic's agent settings (Admin page, Agent permissions) turn this area off for agents.",
    verdict: "design",
    todo: "Change the clinic's agent settings if agents should work here.",
  },
  policy_write: {
    means: "The clinic's agent settings make this area read-only for agents.",
    verdict: "design",
    todo: "Change the clinic's agent settings if agents should make changes here.",
  },
  policy_send: {
    means: "The clinic's agent settings turn this kind of send off for agents.",
    verdict: "design",
    todo: "Change the clinic's agent settings if agents should send it.",
  },
  // agent_settings
  scope_area: {
    means: "The agent's owner didn't give this agent that area.",
    verdict: "design",
    todo: "The owner can edit the agent to add the area.",
  },
  scope_schema: {
    means: "The agent's owner left this clinic out of the agent.",
    verdict: "design",
    todo: "The owner can edit the agent to add the clinic.",
  },
  // cant_do_yet
  no_action: {
    means: "The agent was asked to do something it has no tool for yet. The detail says what.",
    verdict: "fix",
    todo: "A feature request: build the action, or Resolve it if it's out of scope.",
  },
  unknown_action: {
    means: "The agent tried an action that doesn't exist. The Action column has the name it used.",
    verdict: "fix",
    todo: "Usually the model guessing a name: add the action, or make the prompt name the real one.",
  },
  planner_cannot: {
    means: "Someone asked for an agent to do something agents can't do yet. The detail says what.",
    verdict: "fix",
    todo: "A feature request: build it, or Resolve it if it's out of scope.",
  },
  unclear: {
    means: "The request was too vague for the agent to act on.",
    verdict: "design",
    todo: "Usually the person should ask more clearly. Fix only if the request was clear and the agent misread it.",
  },
  // blocked
  needs_approval: {
    means: "In Auto mode the action still waited for approval: the recipient isn't on file, or the clinic's settings ask first for this kind of send.",
    verdict: "design",
    todo: "Nothing to fix: approving it in the agent's chat carries it out.",
  },
  action_refused: {
    means: "The app refused the action because of one of its rules. The detail says which, for example already sent or no phone on file.",
    verdict: "check",
    todo: "If the rule is right, it's by design. If the agent should have known better, improve its prompt. If the rule is wrong, fix the rule.",
  },
  action_limit: {
    means: "The run used all its actions (30 unless changed) before it finished.",
    verdict: "fix",
    todo: "Make the task smaller or the agent more efficient, or raise AGENTS_MAX_ACTIONS.",
  },
  time_limit: {
    means: "The run hit its time limit before it finished.",
    verdict: "fix",
    todo: "Make the task smaller or faster, or raise the agents' time limit.",
  },
  // failed
  bad_arguments: {
    means: "The agent called an action with missing or wrong inputs. The detail says what was wrong.",
    verdict: "fix",
    todo: "Improve the action's description or the agent's prompt so the model fills it in right.",
  },
  unknown_record: {
    means: "The agent referred to a record it hadn't been shown in that run.",
    verdict: "fix",
    todo: "Usually a model slip. If it keeps happening, fix the prompt or the action that should have listed the record.",
  },
  access_unconfirmed: {
    means: "The app couldn't confirm the person's access just then, usually because a database read failed.",
    verdict: "check",
    todo: "Fine if it's rare. If it repeats, check the database and the server's logs.",
  },
  privacy_check: {
    means: "The patient-data scrub couldn't run, so nothing was sent to the AI.",
    verdict: "fix",
    todo: "Check that the server's de-identification model (spaCy en_core_web_sm) loads: its log says why.",
  },
  error: {
    means: "The action failed with an error in our code. The detail names the error.",
    verdict: "fix",
    todo: "A bug: find the run in the server logs by its run id.",
  },
  run_failed: {
    means: "The whole run failed. The detail says why, for example the agent service answered an error or couldn't be reached.",
    verdict: "check",
    todo: "Agent service or network trouble is infrastructure: check Hermes. Anything else is a bug.",
  },
};

const UNKNOWN_HELP = {
  means: "A kind of gap this page doesn't know yet.",
  verdict: "check",
  todo: "Read the detail to decide.",
};

// Whose access refused it, for the reasons that say a member's.
const WHO_WORDS = { acting: "the person the agent acted for", other_member: "another member of the agent" };

// wontfix is what Resolve sets: a gap that isn't an issue, or works as designed.
export const STATUS_WORDS = { new: "New", reviewed: "Reviewed", fixed: "Fixed", wontfix: "Not an issue" };

// The areas in backend/api/agents/types.py's AREA_LABELS, in its order, worded as table labels.
export const AREA_WORDS = {
  fax: "Fax inbox",
  charts: "Charts & schedule",
  tasks: "Tasks & messages",
  outreach: "Patient outreach",
};

// The labels of types.py's TYPES.
export const AGENT_TYPE_WORDS = {
  general: "General",
  orchestrator: "Orchestrator",
  fax: "Fax",
  charts: "Charts & schedule",
  tasks: "Tasks & messages",
  outreach: "Outreach",
  texts: "Patient texts",
  messages: "Team messages",
  forms: "Forms",
  watcher: "Watcher",
};

function words(table, code) {
  if (code === undefined || code === null || code === "") return "-";
  return Object.prototype.hasOwnProperty.call(table, code) ? table[code] : String(code);
}

export const categoryWords = (code) => words(CATEGORY_WORDS, code);
export const reasonWords = (code) => words(REASON_WORDS, code);
export const statusWords = (code) => words(STATUS_WORDS, code);
export const areaWords = (code) => words(AREA_WORDS, code);
export const agentTypeWords = (code) => words(AGENT_TYPE_WORDS, code);
export const reasonHelp = (code) => (
  Object.prototype.hasOwnProperty.call(REASON_HELP, code) ? REASON_HELP[code] : UNKNOWN_HELP
);
export const whoWords = (code) => (Object.prototype.hasOwnProperty.call(WHO_WORDS, code) ? WHO_WORDS[code] : null);
